// components/SearchBar.jsx — OnSumn Spotlight search.
// Window-attached at file bottom per OnSumn convention.
//
// v1 surface: trigger button in Header → dropdown panel below.
// Spec: docs/superpowers/specs/2026-06-03-onsumn-spotlight-search-design.md

// TODO: navigator.platform is deprecated. Modern replacement: navigator.userAgentData?.platform
// with navigator.platform as Safari/Firefox fallback. Cosmetic-only today (badge label).
function isMacPlatform() {
  if (typeof navigator === 'undefined') return false;
  return navigator.platform.toLowerCase().includes('mac');
}

// ---------- helpers ----------

function resolveEventHref(event) {
  // Prefer the in-app /e/[slug] event detail page so tapping a search
  // result lands on the OnSumn event card (with venue context, share,
  // intel, "BUY TICKETS" button to the external URL) rather than dumping
  // the user straight onto Ticketmaster. Marco directive 2026-06-04:
  // "i cannot access the event card it just takes me to ticket master."
  // External purchase URL is fallback only when no slug exists.
  if (typeof event.slug === 'string' && event.slug) return `/e/${event.slug}`;
  if (typeof event.url === 'string' && event.url) return event.url;
  if (typeof event.link === 'string' && event.link) return event.link;
  return '#';
}

function truncate(s, n) {
  if (typeof s !== 'string') return '';
  return s.length <= n ? s : s.slice(0, n - 1) + '…';
}

// ---------- styles ----------

function panelStyle(darkMode, isMobile, dragY, mapMode) {
  // Mobile (<768px): bottom sheet, 65dvh, slides up from the bottom. Page
  // header peeks above behind a dim scrim. Rounded top corners + drag-handle
  // indicator give the standard app-like sheet affordance. zIndex above the
  // fixed Header (1000) and above the scrim (1099).
  if (isMobile) {
    const isDragging = dragY > 0;
    return {
      position: 'fixed',
      left: 0, right: 0,
      bottom: 0,
      top: 'auto',
      width: '100vw',
      height: '65dvh',
      maxHeight: '65dvh',
      background: darkMode ? '#0A0A0A' : '#FFFFFF',
      border: 'none',
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      // Soft upward shadow so the sheet sits visually above the dimmed page.
      boxShadow: darkMode
        ? '0 -8px 32px rgba(0,0,0,0.55)'
        : '0 -8px 32px rgba(0,0,0,0.2)',
      zIndex: 1100,
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'hidden',
      // Translate Y while user is dragging the handle down. On release the
      // wrapper either closes (dragY > threshold) or snaps back (transition
      // re-enabled in the handler).
      transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
      transition: isDragging
        ? 'none'
        : 'transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1)',
      // Subtle entrance — slides up from bottom. Only on first mount; the
      // drag-managed transform takes over once the user starts pulling.
      animation: dragY > 0 ? undefined : 'searchsheet-slide-up 220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
    };
  }
  // Desktop / tablet: dropdown anchored to the trigger.
  return {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    // Desktop grows to 880px to host the detail pane in a second column.
    // Tablet (768-899px) stays compact — detail surface is the FLIP popover.
    // Map mode (Goal B): the panel lives INSIDE the map card, which clips at
    // overflow:hidden — a fitted single-column width keeps it on the glass.
    width: mapMode ? 'min(420px, calc(100vw - 48px))' : 'min(880px, calc(100vw - 32px))',
    maxHeight: '76vh',
    overflowY: 'visible',
    // Light mode panel — solid white + firmer border + deeper shadow so the
    // dropdown reads as a fully opaque sheet against the cream surface
    // beneath. Earlier 4%-transparent fill let surface tint bleed through
    // and the 0.08-alpha border vanished. Dark mode unchanged.
    background: darkMode ? '#1E2020' : '#FFFFFF',
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.14)',
    borderRadius: 16,
    boxShadow: darkMode ? '0 12px 32px rgba(0,0,0,0.5)' : '0 16px 40px rgba(0,0,0,0.18)',
    zIndex: 1001,
    padding: '8px 0',
  };
}

function inputStyle(darkMode) {
  return {
    width: 'calc(100% - 24px)',
    margin: '4px 12px 8px',
    padding: '10px 12px',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: darkMode ? '#fff' : '#0A0A0A',
    fontFamily: 'var(--font-body)',
    fontSize: 16,
    borderBottom: darkMode ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(0,0,0,0.08)',
  };
}

// ---------- sub-components ----------

function SectionHeader({ label, darkMode }) {
  return (
    <li role="presentation" style={{
      padding: '12px 12px 4px',
      fontFamily: 'var(--font-body)',
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: darkMode ? 'var(--fog-blue-light, #7A99AC)' : 'var(--fog-blue-deep, #4A6577)',
    }}>{label}</li>
  );
}

function ResultRow({ event, darkMode, focused, optId, onMouseEnter, onClick }) {
  const href = resolveEventHref(event);
  const focusedBg = focused
    ? (darkMode ? 'rgba(255,210,128,0.08)' : 'rgba(217,148,46,0.08)')
    : 'transparent';
  const focusedBorder = focused ? '4px solid var(--amber-gold)' : '4px solid transparent';
  // Always let the parent decide. Parent intercepts mobile clicks for popover;
  // desktop lets the <a> navigate (parent doesn't preventDefault). When href
  // is '#' we still need to call preventDefault so the page doesn't jump to
  // the top — but defer it inside the parent's handler so popover opens.
  const handleClick = (e) => {
    if (href === '#') e.preventDefault();
    if (typeof onClick === 'function') onClick(e);
  };
  return (
    <li role="option" id={optId} aria-selected={focused} style={{ listStyle: 'none' }} onMouseEnter={onMouseEnter}>
      <a
        href={href}
        onClick={handleClick}
        style={{
          display: 'flex',
          gap: 8,
          padding: '10px 12px',
          textDecoration: 'none',
          color: darkMode ? '#fff' : '#0A0A0A',
          background: focusedBg,
          borderLeft: focusedBorder,
        }}>
        <span style={{ flex: 1, fontFamily: "'Be Vietnam Pro', sans-serif", fontSize: 14, fontWeight: 600, lineHeight: 1.35 }}>
          {truncate(event.title, 42)}
          {event.venue && (
            <span style={{
              display: 'block',
              fontSize: 11, fontWeight: 500, opacity: 0.65,
              marginTop: 2,
            }}>{truncate(event.venue, 48)}</span>
          )}
        </span>
        <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, opacity: 0.7, whiteSpace: 'nowrap', alignSelf: 'flex-start', paddingTop: 1 }}>
          {event.date}
        </span>
      </a>
    </li>
  );
}

function VenueRow({ venue, eventCount, darkMode, focused, optId, onMouseEnter, onClick }) {
  const href = venue.slug ? `/venue/${venue.slug}` : '#';
  const focusedBg = focused
    ? (darkMode ? 'rgba(255,210,128,0.08)' : 'rgba(217,148,46,0.08)')
    : 'transparent';
  const focusedBorder = focused ? '4px solid var(--amber-gold)' : '4px solid transparent';
  const handleClick = (e) => {
    if (href === '#') e.preventDefault();
    if (typeof onClick === 'function') onClick(e);
  };
  return (
    <li role="option" id={optId} aria-selected={focused} style={{ listStyle: 'none' }} onMouseEnter={onMouseEnter}>
      <a
        href={href}
        onClick={handleClick}
        style={{
          display: 'flex',
          gap: 8,
          padding: '10px 12px',
          textDecoration: 'none',
          color: darkMode ? '#fff' : '#0A0A0A',
          alignItems: 'center',
          background: focusedBg,
          borderLeft: focusedBorder,
        }}>
        <span aria-hidden="true">🏟</span>
        <span style={{ flex: 1, fontFamily: "'Be Vietnam Pro', sans-serif", fontSize: 14, fontWeight: 600 }}>
          {venue.canonical}
        </span>
        <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, opacity: 0.7 }}>
          {/* Joined, not interpolated: a null city used to leave an orphan
              "· 4 upcoming". City is now absent rather than guessed, so every
              surface that concatenates one has to survive it being missing. */}
          {[venue.city, `${eventCount} upcoming`].filter(Boolean).join(' · ')}
        </span>
      </a>
    </li>
  );
}

function countUpcomingForVenue(index, venue) {
  if (!index || !venue) return 0;
  const today = new Date().toISOString().slice(0, 10);
  return index.events.filter(e =>
    e.haystack.includes(venue.canonical.toLowerCase()) &&
    e.iso_date && e.iso_date >= today
  ).length;
}

/* The last query, module-scoped so it SURVIVES the panel's unmount.
   SearchPanel mounts as `{open && <SearchPanel/>}`, so before this
   (2026-08-20 /goal, Michael): click-outside / Esc / ✕ unmounted the panel
   and the query state died with it — "when i click out of it it clears the
   entire search results." Now the panel seeds from this memory and writes
   through on every keystroke, so reopening restores the search exactly
   where it was; the restored text arrives SELECTED (macOS Spotlight
   behavior — see the focus effect), so typing fresh replaces it in one
   stroke and starting over costs nothing. Page-session memory only, shared
   across this page's SearchBar instances (only one panel opens at a time):
   no storage, nothing persists across a reload. Gated by
   tests/search-query-persist.test.js. */
let _persistedQuery = '';

function SearchPanel({ darkMode, isMobile, onClose, triggerRef, onResultSelect }) {
  const { index, loading } = window.useSearchIndex();
  const [query, setQuery] = React.useState(() => _persistedQuery);
  // Write-through on every change — covers typing, clears, everything.
  React.useEffect(() => { _persistedQuery = query; }, [query]);
  const [focusedRow, setFocusedRow] = React.useState(-1);
  const [popoverItem, setPopoverItem] = React.useState(null);
  const [popoverRect, setPopoverRect] = React.useState(null);
  // ── Goal B opt-in map mode (addendum R2): ONE optional prop. When present,
  // a CONFIRMED selection (row click, venue click, keyboard Enter) calls
  // onResultSelect(item) instead of navigating / opening the FLIP popover —
  // the map flies to the match. The combobox DOM/ARIA/focus machinery is
  // IDENTICAL in both modes; ids are scoped (map-search-*) only so a map
  // instance can coexist with the Header's page-level panel without
  // duplicate-id violations. Prop ABSENT → byte-equivalent default behavior
  // (anchors navigate, popover works, Enter resolves href) — that default is
  // flows.html's regression gate (tests/map-search.test.js).
  const mapMode = typeof onResultSelect === 'function';
  const idBase = mapMode ? 'map-search' : 'search';
  // Drag-to-dismiss state for the mobile bottom sheet. Touch on the drag
  // handle area tracks Y delta; release past DRAG_CLOSE_THRESHOLD closes
  // the sheet, otherwise it snaps back to 0 via the panel's transition.
  const [dragY, setDragY] = React.useState(0);
  const dragStartY = React.useRef(null);
  const DRAG_CLOSE_THRESHOLD = 80;
  // Layout breakpoint widens beyond Header's `isMobile` (767.98) so tablet
  // portrait and narrow desktop windows (<900px) get the popover instead of
  // a cramped 2-column. Trigger-button affordances still use the Header
  // breakpoint — only the panel layout uses the wider threshold.
  const [narrowLayout, setNarrowLayout] = React.useState(isMobile);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 899.98px)');
    const apply = () => setNarrowLayout(mq.matches);
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else if (mq.addListener) mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else if (mq.removeListener) mq.removeListener(apply);
    };
  }, []);
  // Map mode always takes the single-column narrow layout — the panel sits on
  // the map glass where the 880px two-column dropdown would clip. Identical
  // to narrowLayout when the prop is absent.
  const effNarrow = mapMode ? true : narrowLayout;
  const inputRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const listboxRef = React.useRef(null);

  // Focus on open; a RESTORED query (see _persistedQuery) arrives selected
  // so a fresh search is one keystroke away — nothing lost, nothing sticky.
  React.useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    if (el.value) { try { el.select(); } catch (e) {} }
  }, []);
  React.useEffect(() => { setFocusedRow(-1); }, [query]);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      const target = e.target;
      if (!target) return;
      // Inside the panel itself? Keep open.
      if (panelRef.current && panelRef.current.contains(target)) return;
      // Click on the trigger button? Let its onClick handle the toggle.
      if (triggerRef.current && triggerRef.current.contains(target)) return;
      // Mobile popover is portaled to document.body — clicks inside it
      // would otherwise close the panel underneath. The popover's own
      // backdrop click handler stops propagation, but be safe.
      if (popoverItem) return;
      // A modal layer ABOVE us owns the pointer (2026-08-20, Michael's
      // report: closing ENTRY/MERCH killed the search underneath). The
      // ENTRY / MERCH / SHARE popovers all portal to document.body at
      // z=2000 — outside panelRef — so their backdrop-close mousedown read
      // as "outside" here. All four popovers (and the mobile detail sheet)
      // carry aria-modal="true": while any such dialog is in the DOM,
      // outside-close stands down — the modal's backdrop click closes the
      // MODAL, and the search survives beneath it. mousedown fires before
      // the popover's click-close, so the dialog is still present when
      // this guard runs. Gated by tests/search-query-persist.test.js.
      if (document.querySelector('[aria-modal="true"]')) return;
      // Anything else — close.
      onClose();
    };
    // mousedown fires before click — closing on mousedown feels snappier
    // and matches Apple Spotlight / FilterPopup behavior.
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, triggerRef, popoverItem]);

  const placeholder = isMobile
    ? `Search the Bay…`
    : (mapMode
      // Rhea (co-sign, exact string): the map instance never owns ⌘K, so the
      // hotkey suffix would be a dead affordance here.
      ? `Search artists, venues, or 'this weekend'…`
      : `Search artists, venues, or 'this weekend'…   (${isMacPlatform() ? '⌘K' : 'Ctrl K'})`);

  const tonightEvents = React.useMemo(() => {
    if (!index || query) return [];
    const parsed = window.SearchIndex.parseQuery('tonight');
    return window.SearchIndex.search(parsed, index).events.slice(0, 4);
  }, [index, query]);

  /* Temporal header label — registry-sourced STRING, group-gated render
     (pique Phase 3, 2026-07-28). Lineage: the 2026-07-17 ruling kept this
     header OUT of the registry because a lossCopy() runtime call would
     staleness-drop the label while its rows still rendered (orphaned
     group), and the per-event predicate can't describe a group. Michael's
     2026-07-28 Phase-3 authorization supersedes the CLOSED status, and
     this design honors both recorded objections: only the TEXT comes from
     the registry (LOSS_COPY['tonight-badge'].text — the one place the
     literal lives); the GATE stays what it always was — SearchIndex's own
     temporal filter returning a non-empty same-Pacific-day group. No
     staleness coupling, no per-event predicate on a group label. Degrade:
     registry absent → headerless group (rows still render). */
  const tonightLabel = (typeof window !== 'undefined'
    && window.LOSS_COPY && window.LOSS_COPY['tonight-badge']
    && window.LOSS_COPY['tonight-badge'].text) || null;

  const weekendEvents = React.useMemo(() => {
    if (!index || query) return [];
    const parsed = window.SearchIndex.parseQuery('this weekend');
    return window.SearchIndex.search(parsed, index).events.slice(0, 4);
  }, [index, query]);

  const typedResults = React.useMemo(() => {
    if (!index || !query.trim()) return null;
    const parsed = window.SearchIndex.parseQuery(query);
    return window.SearchIndex.search(parsed, index);
  }, [index, query]);

  // Parallel item array — same indexing as the rendered listbox.
  // Used for keyboard nav, hover preview, and click intercept on mobile.
  const visibleItems = React.useMemo(() => {
    const list = [];
    if (!query) {
      tonightEvents.forEach(e => list.push({ kind: 'event', event: e }));
      weekendEvents.forEach(e => list.push({ kind: 'event', event: e }));
    } else if (typedResults) {
      if (typedResults.venue) {
        list.push({
          kind: 'venue',
          venue: typedResults.venue,
          eventCount: countUpcomingForVenue(index, typedResults.venue),
        });
      }
      // Deep-dive: show ALL matched events, not a 6-cap. The listbox
      // is scrollable and the detail pane updates as the user explores.
      typedResults.events.forEach(e => list.push({ kind: 'event', event: e }));
    }
    return list;
  }, [query, tonightEvents, weekendEvents, typedResults, index]);

  // Auto-focus first result when a typed query produces matches. Lets the
  // detail pane populate immediately so the desktop pane isn't a ghost
  // every keystroke. Empty state stays at -1 (no auto-focus).
  React.useEffect(() => {
    if (query.trim() && focusedRow === -1 && visibleItems.length > 0) {
      setFocusedRow(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, visibleItems.length]);

  const selectedItem = focusedRow >= 0 && focusedRow < visibleItems.length
    ? visibleItems[focusedRow]
    : null;

  // Resolve href for keyboard Enter — falls back to visibleItems.
  const hrefFor = (item) => {
    if (!item) return null;
    if (item.kind === 'venue') return item.venue.slug ? `/venue/${item.venue.slug}` : null;
    return resolveEventHref(item.event);
  };

  const openPopoverForItem = (item, rect) => {
    if (!item) return;
    setPopoverItem(item);
    setPopoverRect(rect || null);
  };

  const handleRowMouseEnter = (idx) => {
    // Wide layout only — hover updates the focused row → detail pane.
    if (effNarrow) return;
    setFocusedRow(idx);
  };

  // Row click handling by viewport:
  //   - Narrow (<900px, mobile + tablet portrait): intercept, capture
  //     trigger rect, open the FLIP popover preview. Popover renders the
  //     full event detail (image, title, venue, date, "Get Tickets" CTA
  //     to external purchase, "View on OnSumn" secondary link to the
  //     event page). z-index bumped above the search sheet so both stack
  //     correctly — close popover to return to sheet, close sheet to
  //     return to page. Marco directive 2026-06-04: pop-up preview rather
  //     than navigating off to ticketmaster directly.
  //   - Desktop (≥900px): let <a> navigate (the row is a real link).
  //     selectedItem was already set via hover/keyboard, so this is the
  //     commit action.
  const handleRowClick = (e, item, idx) => {
    setFocusedRow(idx);
    if (mapMode) {
      // Map mode (R2): a confirmed selection FLIES THE MAP — never navigate,
      // never the FLIP popover. Close the panel and hand focus back to the
      // trigger so keyboard users aren't stranded in an unmounted tree.
      e.preventDefault();
      if (item) onResultSelect(item);
      onClose();
      triggerRef.current?.focus();
      return;
    }
    if (!narrowLayout) return;
    e.preventDefault();
    const rowEl = e.currentTarget.closest('li') || e.currentTarget;
    const rect = rowEl.getBoundingClientRect();
    openPopoverForItem(item, rect);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedRow(r => Math.min(r + 1, visibleItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedRow(r => Math.max(r - 1, -1));
    } else if (e.key === 'Enter') {
      const item = selectedItem;
      if (!item) return;
      if (mapMode) {
        // Map mode: Enter confirms → fly the map (same contract as click).
        e.preventDefault();
        onResultSelect(item);
        onClose();
        triggerRef.current?.focus();
        return;
      }
      if (narrowLayout) {
        e.preventDefault();
        const rowEl = document.getElementById(`${idBase}-opt-${focusedRow}`);
        const rect = rowEl ? rowEl.getBoundingClientRect() : null;
        openPopoverForItem(item, rect);
        return;
      }
      const href = hrefFor(item);
      if (href) {
        e.preventDefault();
        window.location.href = href;
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (popoverItem) {
        // Let the popover's own ESC handler run; nothing for us to do.
        return;
      }
      onClose();
      triggerRef.current?.focus();
    }
  };

  // Keep the focused row visible as the user arrows down a long list.
  React.useEffect(() => {
    if (focusedRow < 0) return;
    const el = document.getElementById(`${idBase}-opt-${focusedRow}`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }, [focusedRow]);

  // Build ordered row list with sequential indices for aria-activedescendant.
  const renderRows = () => {
    let rowIdx = 0;
    const out = [];

    if (loading) {
      out.push(<li key="loading" style={{ padding: 12, opacity: 0.6 }}>Loading…</li>);
      return out;
    }

    if (!query) {
      if (tonightEvents.length === 0 && weekendEvents.length === 0) {
        out.push(<li key="empty" style={{ padding: 12, opacity: 0.6 }}>No upcoming shows — pull up later.</li>);
        return out;
      }
      if (tonightEvents.length > 0 && tonightLabel) out.push(<SectionHeader key="hdr-tonight" label={tonightLabel} darkMode={darkMode} />);
      tonightEvents.forEach((e) => {
        const idx = rowIdx++;
        const item = visibleItems[idx];
        out.push(<ResultRow
          key={`t-${idx}`}
          event={e} darkMode={darkMode}
          focused={focusedRow === idx} optId={`${idBase}-opt-${idx}`}
          onMouseEnter={() => handleRowMouseEnter(idx)}
          onClick={(ev) => handleRowClick(ev, item, idx)}
        />);
      });
      if (weekendEvents.length > 0) out.push(<SectionHeader key="hdr-weekend" label="THIS WEEKEND" darkMode={darkMode} />);
      weekendEvents.forEach((e) => {
        const idx = rowIdx++;
        const item = visibleItems[idx];
        out.push(<ResultRow
          key={`w-${idx}`}
          event={e} darkMode={darkMode}
          focused={focusedRow === idx} optId={`${idBase}-opt-${idx}`}
          onMouseEnter={() => handleRowMouseEnter(idx)}
          onClick={(ev) => handleRowClick(ev, item, idx)}
        />);
      });
      return out;
    }

    // Typed state — all matches, scrollable.
    if (typedResults) {
      if (typedResults.venue) {
        out.push(<SectionHeader key="hdr-venues" label="VENUES" darkMode={darkMode} />);
        const idx = rowIdx++;
        const item = visibleItems[idx];
        out.push(<VenueRow
          key={`v-${idx}`}
          venue={typedResults.venue}
          eventCount={countUpcomingForVenue(index, typedResults.venue)}
          darkMode={darkMode}
          focused={focusedRow === idx}
          optId={`${idBase}-opt-${idx}`}
          onMouseEnter={() => handleRowMouseEnter(idx)}
          onClick={(ev) => handleRowClick(ev, item, idx)}
        />);
      }
      if (typedResults.events.length > 0) {
        out.push(<SectionHeader
          key="hdr-events"
          label={`EVENTS · ${typedResults.events.length}`}
          darkMode={darkMode}
        />);
      }
      typedResults.events.forEach((e) => {
        const idx = rowIdx++;
        const item = visibleItems[idx];
        out.push(<ResultRow
          key={`e-${idx}`}
          event={e} darkMode={darkMode}
          focused={focusedRow === idx} optId={`${idBase}-opt-${idx}`}
          onMouseEnter={() => handleRowMouseEnter(idx)}
          onClick={(ev) => handleRowClick(ev, item, idx)}
        />);
      });
      if (!typedResults.venue && typedResults.events.length === 0) {
        out.push(<li key="no-match" style={{ padding: 12, fontSize: 13, opacity: 0.7 }}>
          Nothing for "{query}" — try a venue, an artist, a category, or "this weekend".
        </li>);
      }
    }
    return out;
  };

  const listColStyle = {
    listStyle: 'none', margin: 0, padding: 0,
    flex: effNarrow ? '1 1 auto' : '0 0 420px',
    // Map-mode dropdown caps the list so the panel fits INSIDE the shortest
    // map card (480px) — the list scrolls, the input row stays put.
    maxHeight: (mapMode && !isMobile) ? 264 : (effNarrow ? 'none' : 'calc(76vh - 60px)'),
    // Mobile full-screen sheet needs internal scroll (parent is fixed 100dvh
    // flex column). Tablet/desktop keep their original behavior.
    overflowY: isMobile ? 'auto' : ((mapMode || !effNarrow) ? 'auto' : 'visible'),
    WebkitOverflowScrolling: isMobile ? 'touch' : undefined,
    // Subtle scroll padding so the active row never butts against the edge
    scrollPaddingTop: 8, scrollPaddingBottom: 8,
  };

  const detailColStyle = {
    flex: '1 1 auto',
    minWidth: 0,
    maxHeight: 'calc(76vh - 60px)',
    overflowY: 'auto',
    padding: '12px 16px 16px',
    borderLeft: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
  };

  // Shared input props — rendered either bare (desktop) or wrapped in a
  // header bar with an X close (mobile full-screen sheet).
  const inputProps = {
    ref: inputRef,
    type: 'search',
    role: 'combobox',
    'aria-expanded': 'true',
    'aria-controls': `${idBase}-listbox`,
    'aria-haspopup': 'listbox',
    'aria-autocomplete': 'list',
    'aria-activedescendant': focusedRow >= 0 ? `${idBase}-opt-${focusedRow}` : undefined,
    value: query,
    onChange: (e) => setQuery(e.target.value),
    onKeyDown: handleKeyDown,
    placeholder,
    'aria-label': 'Search OnSumn',
  };

  // Mobile sheet renders via React Portal to document.body — the Header has
  // backdrop-filter which creates a containing block, so `position: fixed`
  // inside the Header would anchor to the Header, not the viewport. That
  // bug shipped 2026-06-04 (commits f0e3ea9/dcc54f4) and put the sheet's
  // top above the visible viewport — search input was offscreen and only
  // mid-sheet results were visible. Portaling to document.body sidesteps it.
  const panel = (
    <>
      {isMobile && (
        // Dim scrim behind the bottom sheet. Page header peeks above through
        // the dim. Tap dismisses (also covered by the click-outside handler
        // above; this is the explicit affordance).
        <div
          onClick={onClose}
          aria-hidden="true"
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.55)',
            zIndex: 1099,
            animation: 'searchsheet-fade-in 180ms ease-out',
          }}
        />
      )}
      <div ref={panelRef} style={panelStyle(darkMode, isMobile, dragY, mapMode)}>
      {isMobile && (
        // Drag-handle row — tap dismisses immediately, drag-down past
        // 80px dismisses on release. Spans full width so the touch target
        // is generous (not just the 36px pill). Cursor: pointer + role=
        // button for explicit affordance.
        <div
          role="button"
          tabIndex={0}
          aria-label="Drag down or tap to close search"
          onClick={onClose}
          onTouchStart={(e) => {
            dragStartY.current = e.touches[0].clientY;
          }}
          onTouchMove={(e) => {
            if (dragStartY.current == null) return;
            const dy = e.touches[0].clientY - dragStartY.current;
            // Only respond to downward drags. Cap at 400px so an aggressive
            // pull doesn't translate the sheet off-screen weirdly before
            // the release closes it.
            if (dy > 0) setDragY(Math.min(dy, 400));
          }}
          onTouchEnd={() => {
            if (dragY > DRAG_CLOSE_THRESHOLD) {
              onClose();
            }
            // Snap back (or stay closed); the transition handles the animation.
            setDragY(0);
            dragStartY.current = null;
          }}
          onTouchCancel={() => {
            setDragY(0);
            dragStartY.current = null;
          }}
          style={{
            flex: '0 0 auto',
            display: 'flex', justifyContent: 'center',
            padding: '10px 0 6px',
            cursor: 'pointer',
            touchAction: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
          }}
        >
          <div aria-hidden="true" style={{
            width: 44, height: 5,
            borderRadius: 3,
            background: darkMode ? 'rgba(255,255,255,0.32)' : 'rgba(0,0,0,0.22)',
          }} />
        </div>
      )}
      {isMobile ? (
        // Mobile sheet header — X close + input on a single row. No safe-
        // area-inset-top needed because the sheet anchors to the bottom,
        // not the top.
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
          flex: '0 0 auto',
        }}>
          <button
            onClick={onClose}
            type="button"
            aria-label="Close search"
            className="focus-double-ring"
            style={{
              minWidth: 44, minHeight: 44,
              background: 'transparent',
              border: 'none',
              color: darkMode ? '#fff' : '#0A0A0A',
              fontSize: 26, lineHeight: 1,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 8,
              padding: 0,
            }}
          >×</button>
          <input
            {...inputProps}
            style={{
              flex: '1 1 auto',
              minWidth: 0,
              padding: '12px 8px',
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: darkMode ? '#fff' : '#0A0A0A',
              fontFamily: 'var(--font-body)',
              // 16px+ to suppress iOS Safari focus-zoom on inputs.
              fontSize: 16,
            }}
          />
        </div>
      ) : (
        <input
          {...inputProps}
          style={inputStyle(darkMode)}
        />
      )}
      <div role="status" aria-live="polite" style={{
        position: 'absolute', left: -10000, top: 'auto', width: 1, height: 1, overflow: 'hidden',
      }}>
        {query && typedResults && (
          `${typedResults.events.length} events${typedResults.venue ? ', 1 venue match' : ''}`
        )}
      </div>

      {effNarrow ? (
        <ul ref={listboxRef} id={`${idBase}-listbox`} role="listbox" style={listColStyle}>
          {renderRows()}
        </ul>
      ) : (
        <div style={{
          display: 'flex',
          gap: 0,
          maxHeight: 'calc(76vh - 60px)',
          overflow: 'hidden',
        }}>
          <ul ref={listboxRef} id={`${idBase}-listbox`} role="listbox" style={listColStyle}>
            {renderRows()}
          </ul>
          <div style={detailColStyle} aria-live="polite">
            <window.SearchDetailPane item={selectedItem} darkMode={darkMode} />
          </div>
        </div>
      )}

      {effNarrow && popoverItem && (
        <window.SearchDetailPopover
          item={popoverItem}
          triggerRect={popoverRect}
          darkMode={darkMode}
          onClose={() => {
            setPopoverItem(null);
            setPopoverRect(null);
            // Mobile only — single-tap exit. Closing the popover also
            // dismisses the underlying search sheet so user lands back
            // on the page instead of being parked in search. Marco
            // directive 2026-06-04: "the amount of steps it takes to
            // exit out." Tablet (768-899px) keeps the popover→sheet
            // chain so users can browse other results.
            if (isMobile) onClose();
          }}
        />
      )}
      </div>
    </>
  );

  // Portal escape — mobile only. Desktop dropdown intentionally stays
  // in-tree so its position: absolute anchors to the SearchBar wrapper.
  if (isMobile
      && typeof window !== 'undefined'
      && window.ReactDOM
      && typeof window.ReactDOM.createPortal === 'function'
      && typeof document !== 'undefined') {
    return window.ReactDOM.createPortal(panel, document.body);
  }
  return panel;
}

// ---------- hotkey badge ----------

function HotkeyBadge({ darkMode }) {
  const label = isMacPlatform() ? '⌘K' : 'Ctrl K';
  return (
    <span aria-hidden="true" style={{
      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      fontSize: 11,
      opacity: 0.6,
      padding: '2px 6px',
      border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.12)',
      borderRadius: 4,
      marginLeft: 'auto',
    }}>{label}</span>
  );
}

// ---------- main component ----------

function SearchBar({ darkMode, isMobile, onResultSelect }) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef(null);
  // Map mode: no global hotkeys and no ⌘K chrome (badge) — the Header's
  // page-level instance owns the shortcut (co-sign ratified 2026-07-21).
  const mapMode = typeof onResultSelect === 'function';

  React.useEffect(() => {
    // Map-mode instances (onResultSelect present) never register the global
    // ⌘K / "/" hotkeys: the Header's page-level SearchBar already owns them,
    // and a second registration would double-open two combobox panels on one
    // keystroke.
    if (mapMode) return undefined;
    const handler = (e) => {
      const target = e.target;
      const inField = target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        (target.getAttribute && target.getAttribute('contenteditable') === 'true')
      );
      if (inField) return;

      // Cmd+K or Ctrl+K — open panel.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
        return;
      }
      // "/" — open (when not inside a field).
      if (e.key === '/' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onResultSelect]);

  const handleToggle = () => setOpen(o => !o);

  return (
    <div style={{
      position: 'relative',
      display: isMobile ? 'flex' : 'inline-flex',
      alignItems: 'center',
      // Mobile: extend across the slack between ONSUMN and the +/☰ icons
      // (Marco directive 2026-06-04). Right-cluster also flex-grows to
      // claim the slack so this trigger can fill it.
      flex: isMobile ? '1 1 auto' : 'initial',
      minWidth: 0,
    }}>
      <button
        ref={triggerRef}
        onClick={handleToggle}
        type="button"
        aria-label={open ? 'Close search' : 'Open search'}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="focus-double-ring search-trigger"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          // Left-align across mobile + desktop. Tried center on mobile
          // (Marco request 2026-06-04) then reverted — felt off-balance
          // in the extended pill. Icon stays at left edge of the pill,
          // label follows, slack to the right.
          justifyContent: 'flex-start',
          gap: 8,
          // Mobile: extended pill — width:100% so the trigger fills the
          // flex-grown wrapper (which claims the slack between ONSUMN and
          // the +/☰ icons). Search-led decision 2026-06-04 — discovery is
          // the primary affordance on mobile, contribution (+) recedes to
          // neutral glass.
          padding: isMobile ? '10px 14px' : '8px 12px',
          minWidth: 0,
          width: isMobile ? '100%' : undefined,
          minHeight: 44,
          borderRadius: 999,
          // Light mode: solid white pill + stronger ink border + soft drop
          // shadow so the bar reads as a distinct affordance against the
          // #F8F8F8 / venue-cream surfaces. Mobile gets a subtle international-
          // orange border tint instead of plain ink so it visually owns
          // "search me first" without screaming.
          border: isMobile
            ? '1px solid rgba(255,79,0,0.42)'
            : (darkMode ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(0,0,0,0.18)'),
          background: darkMode ? '#1E2020' : '#FFFFFF',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          boxShadow: darkMode ? 'none' : '0 1px 2px rgba(0,0,0,0.06)',
          color: darkMode ? '#fff' : '#0A0A0A',
          fontFamily: 'var(--font-body)',
          fontSize: isMobile ? 10 : 14,
          fontWeight: isMobile ? 700 : 400,
          letterSpacing: isMobile ? '0.08em' : 'normal',
          textTransform: isMobile ? 'uppercase' : 'none',
          cursor: 'pointer',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <span>{isMobile ? 'Search' : 'Search'}</span>
        {!isMobile && !open && !mapMode && <HotkeyBadge darkMode={darkMode} />}
      </button>
      {open && <SearchPanel darkMode={darkMode} isMobile={isMobile} onClose={() => setOpen(false)} triggerRef={triggerRef} onResultSelect={onResultSelect} />}
    </div>
  );
}

window.SearchBar = SearchBar;
// Test-only: clears the module's persisted-query memory so one vitest file
// can exercise both the cold-open and the restored-open paths. Never called
// in page code.
window.SearchBar.__resetQueryMemoryForTests = () => { _persistedQuery = ''; };
